import { Vector3 } from 'three';
import { gravityBankDefinable } from './orientation';

/** Bank angle is its own channel, keyed by arc length — control points define position only. */
export interface RollKey {
  s: number;
  deg: number;
  interp: 'smooth' | 'linear';
}

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

/** Signed delta in (−180, 180] from `from` to `to` — the short way around the circle. */
export function shortestAngleDelta(from: number, to: number): number {
  return ((((to - from) % 360) + 540) % 360) - 180;
}

/**
 * Interpolated roll (degrees) at arc length s.
 *
 * Consecutive override keys always take the shortest turn (≤180°). A 360°
 * banking twist therefore needs three pins (e.g. 0° / 180° / 360°). Keys are
 * unwrapped along that short path so 0° → 180° → 360° stays a continuous
 * full turn instead of unwinding. Closed-track wrap (last → first) also
 * takes the shortest rotation.
 */
export function rollAt(keys: RollKey[], s: number, totalLength: number, closed: boolean): number {
  if (keys.length === 0) return 0;
  const sorted = keys; // callers keep keys sorted by s
  if (keys.length === 1) return keys[0].deg;

  const unwrapped = unwrapRollKeys(sorted);
  const first = unwrapped[0]!;
  const last = unwrapped[unwrapped.length - 1]!;

  if (s <= first.s) {
    if (!closed) return first.deg;
    const span = first.s + totalLength - last.s;
    const t = span > 1e-9 ? (s + totalLength - last.s) / span : 1;
    return interpPair(last, first, t, true);
  }
  if (s >= last.s) {
    if (!closed) return last.deg;
    const span = first.s + totalLength - last.s;
    const t = span > 1e-9 ? (s - last.s) / span : 0;
    return interpPair(last, first, t, true);
  }
  // binary search for the key pair around s
  let lo = 0;
  let hi = unwrapped.length - 1;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (unwrapped[mid]!.s <= s) lo = mid;
    else hi = mid;
  }
  const a = unwrapped[lo]!;
  const b = unwrapped[hi]!;
  const t = b.s - a.s > 1e-9 ? (s - a.s) / (b.s - a.s) : 0;
  // already unwrapped onto the short path — lerp the continuous degrees
  return interpPair(a, b, t, false);
}

/** Rewrite key degrees so each step from the previous is the short way around. */
function unwrapRollKeys(keys: RollKey[]): RollKey[] {
  if (keys.length <= 1) return keys;
  const out: RollKey[] = [{ ...keys[0]! }];
  for (let i = 1; i < keys.length; i++) {
    const prev = out[i - 1]!.deg;
    out.push({ ...keys[i]!, deg: prev + shortestAngleDelta(prev, keys[i]!.deg) });
  }
  return out;
}

function interpPair(a: RollKey, b: RollKey, t: number, shortest: boolean): number {
  t = Math.min(Math.max(t, 0), 1);
  const f = b.interp === 'linear' ? t : smoothstep(t);
  const delta = shortest ? shortestAngleDelta(a.deg, b.deg) : b.deg - a.deg;
  return a.deg + delta * f;
}

export function sortRollKeys(keys: RollKey[]): RollKey[] {
  return [...keys].sort((a, b) => a.s - b.s);
}

/** Arc-length radius for treating a roll key as belonging to a track node. */
export const NODE_BANK_MERGE_M = 0.75;

/** Index of the roll key nearest to `s` within `maxDist`, or -1. */
export function nearestRollKeyIndex(keys: RollKey[], s: number, maxDist = NODE_BANK_MERGE_M): number {
  let best = -1;
  let bestD = maxDist;
  for (let i = 0; i < keys.length; i++) {
    const d = Math.abs(keys[i].s - s);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

/** True when a roll key is pinned at this arc length (node bank override). */
export function hasRollKeyAt(keys: RollKey[], s: number, maxDist = NODE_BANK_MERGE_M): boolean {
  return nearestRollKeyIndex(keys, s, maxDist) >= 0;
}

/**
 * Bank at `s` as if the key (if any) belonging to that station were removed —
 * the interpolated / auto value for the Track panel.
 */
export function autoBankAt(
  keys: RollKey[],
  s: number,
  totalLength: number,
  closed: boolean,
  maxDist = NODE_BANK_MERGE_M,
): number {
  const ki = nearestRollKeyIndex(keys, s, maxDist);
  if (ki < 0) return rollAt(keys, s, totalLength, closed);
  const without = keys.filter((_, i) => i !== ki);
  return rollAt(without, s, totalLength, closed);
}

/**
 * Pin bank at arc length `s` to `deg`, reusing a nearby key or inserting one.
 * Returns the updated (sorted) key list.
 */
export function setRollKeyAt(keys: RollKey[], s: number, deg: number, maxDist = NODE_BANK_MERGE_M): RollKey[] {
  const ki = nearestRollKeyIndex(keys, s, maxDist);
  if (ki >= 0) {
    const next = keys.map((k, i) => (i === ki ? { ...k, s, deg } : k));
    return sortRollKeys(next);
  }
  return sortRollKeys([...keys, { s, deg, interp: 'smooth' }]);
}

/** Remove the roll key belonging to arc length `s`, if any. */
export function clearRollKeyAt(keys: RollKey[], s: number, maxDist = NODE_BANK_MERGE_M): RollKey[] {
  const ki = nearestRollKeyIndex(keys, s, maxDist);
  if (ki < 0) return keys;
  return keys.filter((_, i) => i !== ki);
}

/**
 * Copy `forcedBankDeg` onto roll keys (no RMF conversion). Production frame
 * builds use `authoredRollKeysFromTrack`, which treats node banks as gravity-
 * relative and converts them into this track-relative channel.
 * Nodes whose tangent is too vertical to define gravity bank are skipped.
 */
export function rollKeysFromNodes(
  track: { nodes: { forcedBankDeg?: number }[] },
  spanStartS: (i: number) => number,
  tanAt?: (i: number) => Vector3,
): RollKey[] {
  const keys: RollKey[] = [];
  for (let i = 0; i < track.nodes.length; i++) {
    const deg = track.nodes[i].forcedBankDeg;
    if (deg === undefined) continue;
    if (tanAt && !gravityBankDefinable(tanAt(i))) continue;
    const s = spanStartS(i);
    if (!isFinite(s)) continue;
    keys.push({ s, deg, interp: 'smooth' });
  }
  return sortRollKeys(keys);
}

/** Bank at a node as if its own forced bank were cleared (neighbours only). */
export function autoBankAtNode(
  track: { closed: boolean; nodes: { forcedBankDeg?: number }[] },
  nodeIndex: number,
  spanStartS: (i: number) => number,
  totalLength: number,
  tanAt?: (i: number) => Vector3,
): number {
  const s = spanStartS(nodeIndex);
  if (!isFinite(s)) return 0;
  const keys = rollKeysFromNodes(
    {
      nodes: track.nodes.map((n, i) =>
        i === nodeIndex ? { ...n, forcedBankDeg: undefined } : n,
      ),
    },
    spanStartS,
    tanAt,
  );
  return rollAt(keys, s, totalLength, track.closed);
}

/**
 * Auto-bank: write roll keys over [s0, s1] from the simulated speed profile so
 * that the lateral G is reduced by `fraction` (1 = fully banked / no lateral G,
 * 0 = flat). Emits ordinary keys the user can edit afterwards.
 */
export function autoBankKeys(
  s0: number,
  s1: number,
  speedAt: (s: number) => number,
  lateralCurvatureAt: (s: number) => number, // signed: + = curving right
  gravity: number,
  fraction: number,
  spacing = 6,
): RollKey[] {
  const keys: RollKey[] = [];
  const n = Math.max(2, Math.ceil((s1 - s0) / spacing));
  for (let i = 0; i <= n; i++) {
    const s = s0 + ((s1 - s0) * i) / n;
    const v = speedAt(s);
    const k = lateralCurvatureAt(s);
    const bank = Math.atan2(v * v * k, gravity) * fraction;
    keys.push({ s, deg: (bank * 180) / Math.PI, interp: 'smooth' });
  }
  return keys;
}
